import { AppConstant, FormatConstant, KeyConstant, SystemConstant } from "const";
import store, {
  AttachmentActions,
  ConversationActions,
  ConversationSelectors,
  getReduxState,
  SystemActions,
} from "redux-store";
import { getInteractor } from "services/local.service";
import { uploadFile } from "services/multiple-file";
import StringFormat from "string-format";
import { convertString2JSON, StorageUtil, toSnake } from "utils";
import { getCommonLang } from "utils/lang.utils";
import { DISPLAY_MSG_TYPES } from "./saga.helper";

export const sendFileMessage = async action => {
  try {
    const { data, prefixKey } = action;

    // Save message in local
    const currentMessage = data.currentMessage;
    await getInteractor(prefixKey).LocalMessageService.save([
      {
        ...currentMessage,
        message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.fileSending,
      },
    ]);

    // Upload to tus server
    const multipleFile = convertString2JSON(data.content, {}).multiple_file || [];

    const domain = StorageUtil.getCommonKey(KeyConstant.KEY_SELECTED_SERVER).domain;
    window.electronEvent.emitEvent(AppConstant.TRIOS_EMIT_EVENT_KEY.saveUploadingFiles, {
      attachmentIds: multipleFile.map(item => item.attachment_id),
      baseUrl: StringFormat(FormatConstant.FM_BASE_URL, domain),
      accessToken: StorageUtil.getItem(KeyConstant.KEY_TOKEN, prefixKey),
      prefixKey: prefixKey,
    });

    let isUploadSuccess = true;
    for (let index = 0; index < multipleFile.length; index++) {
      const file = multipleFile[index];
      const fileRecord = await getInteractor(prefixKey).LocalFileService.get(file.attachment_id);
      if (fileRecord && fileRecord?.status === SystemConstant.UPLOAD_STATUS.success) {
        continue;
      }

      const uploadResult = await uploadFile(
        prefixKey,
        file.attachment_id,
        SystemConstant.UPLOAD_TYPE.group,
        data.groupId,
        {
          onError: handleUploadFileMsgError,
          onProgress: handleUploadFileMsgProgress,
          onSuccess: handleUploadFileMsgSuccess,
        },
      );

      if (uploadResult === SystemConstant.UPLOAD_STATUS.failed) {
        isUploadSuccess = false;
      }

      window.electronEvent.emitEvent(AppConstant.TRIOS_EMIT_EVENT_KEY.saveUploadingFiles, {
        attachmentIds: multipleFile.map(item => item.attachment_id).filter(item => item !== file.attachment_id),
        baseUrl: StringFormat(FormatConstant.FM_BASE_URL, domain),
        accessToken: StorageUtil.getItem(KeyConstant.KEY_TOKEN, prefixKey),
        prefixKey: prefixKey,
      });
    }

    // All file upload success => Send message
    if (isUploadSuccess) {
      store.dispatch(ConversationActions.sendMessage(data, prefixKey));
    } else {
      // Update local message
      const updateValues = {
        modified: Date.now(),
        message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.fileSendFailed,
        status: SystemConstant.MESSAGE_STATUS.failed,
      };
      const updateLocalMessage = toSnake({
        ...currentMessage,
        ...updateValues,
      });

      await getInteractor(prefixKey).LocalMessageService.updateMessage(currentMessage.id, updateValues);

      // Trigger to UI
      const selectedGroupId = getReduxState(ConversationSelectors.getSelectedGroupId);
      if (updateLocalMessage.group_id === selectedGroupId && DISPLAY_MSG_TYPES.includes(updateLocalMessage.send_type)) {
        store.dispatch(ConversationActions.receivedRemoteMessage(updateLocalMessage));
      }
      throw new Error("Upload file failed");
    }
  } catch (error) {
    console.error(error);
    store.dispatch(
      SystemActions.systemSet({
        toast: {
          content: getCommonLang("TXT_SERVER_ERROR"),
          severity: "error",
          modified: Date.now(),
        },
      }),
    );
  }
};

const handleUploadFileMsgError = async (prefixKey, attachmentId) => {
  await getInteractor(prefixKey).LocalFileService.update(
    { status: SystemConstant.UPLOAD_STATUS.failed, modified: Date.now() },
    { id: attachmentId },
  );

  return SystemConstant.UPLOAD_STATUS.failed;
};

const handleUploadFileMsgProgress = (bytesUploaded, bytesTotal, attachmentId) => {
  store.dispatch(
    AttachmentActions.updateAttachmentProgress(
      attachmentId,
      bytesUploaded,
      bytesTotal,
      SystemConstant.FILE_OPERATION.uploading,
    ),
  );
};

const handleUploadFileMsgSuccess = async (prefixKey, attachmentId) => {
  await getInteractor(prefixKey).LocalFileService.update(
    { status: SystemConstant.UPLOAD_STATUS.success, modified: Date.now() },
    { id: attachmentId },
  );
  return SystemConstant.UPLOAD_STATUS.success;
};
